import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { appConfig } from '@common-config/app.config';
import { namespace } from '@common-config/constants';
import {
  backendApi,
  type AgentTik,
  type TikAccessory,
  type TikCategory,
  type TikContent,
  type TikShape,
} from '@common/reducers/backend-api/backend-api';
import { Loader2, Plus, Sparkles, Trash2, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * "Publish as a Tik" (docs/tiks-plan.md v3 §5) — the agent's public marketplace profile.
 *
 * The AI fills the profile from the agent's instructions; the owner edits it and sees a
 * LIVE preview of the real website card/page (an iframe of `<site>/tiks/preview`, fed by
 * postMessage — the same components as the public pages, never a copy). Publishing needs
 * the explicit "my number will be public" consent. Whether it is actually listed is the
 * backend's derived `status` — the panel just says what's missing.
 */
const SHAPES: TikShape[] = ['round', 'squircle', 'egg', 'gumdrop', 'bun'];
const ACCESSORIES: TikAccessory[] = [
  'headphones', 'sunnies', 'polish', 'scissors', 'partyHat', 'polaroid', 'swatches',
  'tattooKit', 'chefHat', 'nightcap', 'gradCap', 'saladBowl', 'sweatband', 'sprout',
];
const CATEGORIES: TikCategory[] = ['Create', 'Look', 'Health', 'Home', 'Family', 'Learn'];
const MAX_LINES = 8;

/** Where the website lives (its /tiks/preview renders the draft). */
const SITE = (appConfig.TIKS_SITE ?? appConfig.HOMEPAGE).replace(/\/+$/, '');
/** WhatsApp Web's CSP forbids framing other sites, so the extension can't show the preview inline. */
const canFramePreview = () => typeof window !== 'undefined' && window.location.hostname !== 'web.whatsapp.com';

/** The backend's handle rule (services/tiks/tik-profile.ts HANDLE_RE). */
const HANDLE_RE = /^[a-z][a-z0-9-]{1,22}[a-z0-9]$/;
const slug = (label: string) => {
  const s = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^[^a-z]+/, '').slice(0, 24).replace(/-+$/, '');
  return HANDLE_RE.test(s) ? s : '';
};

/** The first field the backend would reject (null = saveable). */
function invalidField(d: TikContent): string | null {
  if (!HANDLE_RE.test(d.handle)) return 'handle';
  for (const k of ['name', 'profession', 'tagline', 'bio', 'starter'] as const) if (!d[k].trim()) return k;
  if (!d.services.some((x) => x.trim())) return 'services';
  return null;
}

const emptyDraft = (label: string): TikContent => ({
  handle: slug(label),
  name: label.slice(0, 24),
  profession: '',
  tagline: '',
  bio: '',
  services: [''],
  category: 'Create',
  look: { color: '#7B5CFF', shape: 'round', accessory: 'headphones' },
  starter: '',
  sampleChat: [{ from: 'user', text: '' }, { from: 'tik', text: '' }],
});

/** Only what the backend accepts (drop empty rows a half-filled form leaves behind). */
const cleanDraft = (d: TikContent): TikContent => ({
  ...d,
  services: d.services.map((s) => s.trim()).filter(Boolean),
  sampleChat: d.sampleChat.filter((l) => l.text?.trim() || l.artifact).map((l) => ({ ...l, text: l.text?.trim() })),
});

const errorMessage = (err: unknown, fallback: string): string => {
  const m = (err as { data?: { message?: unknown } })?.data?.message;
  return typeof m === 'string' && m ? m : fallback;
};

export interface TikSectionProps {
  agentId: string;
  agentLabel: string;
  classes: { section: string; title: string; label: string; hint: string; input: string };
}

export const TikSection: React.FC<TikSectionProps> = ({ agentId, agentLabel, classes }) => {
  const { t } = useTranslation(namespace);
  const { data, isLoading } = backendApi.useGetMyBridgeAgentTikQuery({ agentId });
  const [saveTik, { isLoading: saving }] = backendApi.useSaveMyBridgeAgentTikMutation();
  const [draftTik, { isLoading: drafting }] = backendApi.useDraftMyBridgeAgentTikMutation();
  const [deleteTik, { isLoading: deleting }] = backendApi.useDeleteMyBridgeAgentTikMutation();
  const [draft, setDraft] = useState<TikContent | null>(null);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'card' | 'profile'>('card');
  const frame = useRef<HTMLIFrameElement | null>(null);

  const stored = data?.tik ?? null;
  const status = data?.status ?? null;
  // The stored profile is the editor's starting point (once, and again after a delete).
  useEffect(() => {
    if (stored && !draft) {
      const { published: _p, review: _r, phoneConsentAt, ...content } = stored;
      setDraft(content);
      setConsent(!!phoneConsentAt);
    }
  }, [stored]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live preview: the page says 'ready' once loaded; from then on every change is pushed to it.
  const [previewReady, setPreviewReady] = useState(false);
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin === SITE && (e.data as { type?: string })?.type === 'tik-preview-ready') setPreviewReady(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  useEffect(() => {
    if (previewReady) frame.current?.contentWindow?.postMessage({ type: 'tik-preview', tik: { ...draft, live: !!status?.live }, view }, SITE);
  }, [previewReady, draft, view, status?.live]);

  const set = (patch: Partial<TikContent>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const setLook = (patch: Partial<TikContent['look']>) => setDraft((d) => (d ? { ...d, look: { ...d.look, ...patch } } : d));

  const save = async (published?: boolean) => {
    if (!draft) return;
    setError(null);
    const bad = invalidField(cleanDraft(draft));
    if (bad) {
      const field = { field: t(`myBridge.tik.f_${bad}`) };
      setError(bad === 'handle' ? t('myBridge.tik.errorHandle', field) : t('myBridge.tik.errorRequired', field));
      return;
    }
    try {
      await saveTik({ agentId, content: cleanDraft(draft), ...(published !== undefined ? { published } : {}), phoneConsent: consent }).unwrap();
    } catch (err) {
      setError(errorMessage(err, t('myBridge.tik.errorSave')));
    }
  };

  const fillWithAi = async () => {
    setError(null);
    if (draft && !window.confirm(t('myBridge.tik.redraftConfirm'))) return;
    try {
      const next = await draftTik({ agentId }).unwrap();
      // A published Tik keeps its link (a new handle = a new URL and a new review).
      setDraft(stored ? { ...next, handle: stored.handle } : next);
    } catch (err) {
      setError(errorMessage(err, t('myBridge.tik.errorDraft')));
    }
  };

  const remove = async () => {
    if (!window.confirm(t('myBridge.tik.deleteConfirm'))) return;
    try {
      await deleteTik({ agentId }).unwrap();
      setDraft(null);
      setConsent(false);
    } catch (err) {
      setError(errorMessage(err, t('myBridge.tik.errorSave')));
    }
  };

  const { section, title, label, hint, input } = classes;
  const missing = (status?.missing ?? []).filter((m) => m !== 'published');
  if (isLoading) return <div className={section}><Loader2 className="h-4 w-4 animate-spin text-ai-text-secondary" /></div>;

  if (!draft) {
    return (
      <div className={section} data-testid="tik-section">
        <div className={title}>{t('myBridge.tik.title')}</div>
        <p className={hint}>{t('myBridge.tik.intro')}</p>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="ai" size="sm" onClick={fillWithAi} disabled={drafting} data-testid="tik-draft-ai">
            {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {t('myBridge.tik.draftAi')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDraft(emptyDraft(agentLabel))} data-testid="tik-draft-manual">
            {t('myBridge.tik.draftManual')}
          </Button>
        </div>
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  }

  const field = (key: 'name' | 'handle' | 'profession' | 'tagline' | 'starter', max: number) => (
    <div className="space-y-1">
      <Label className={label} htmlFor={`tik-${key}`}>{t(`myBridge.tik.f_${key}`)}</Label>
      <Input id={`tik-${key}`} value={draft[key]} maxLength={max} onChange={(e) => set({ [key]: e.target.value })} className={input} data-testid={`tik-${key}`} dir="auto" />
    </div>
  );
  const select = <T extends string>(id: string, value: T, options: readonly T[], onChange: (v: T) => void) => (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value as T)} className={`h-10 w-full rounded-md border px-2 text-sm ${input}`} data-testid={id}>
      {options.map((o) => <option key={o} value={o}>{t(`myBridge.tik.opt_${o}`, { defaultValue: o })}</option>)}
    </select>
  );

  return (
    <div className={section} data-testid="tik-section">
      <div className="flex items-center justify-between gap-2">
        <div className={title}>{t('myBridge.tik.title')}</div>
        <TikStatusBadge status={status} published={!!stored?.published} />
      </div>
      <p className={hint}>{t('myBridge.tik.editHint')}</p>

      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {field('name', 24)}
            {field('handle', 24)}
            {field('profession', 32)}
            <div className="space-y-1">
              <Label className={label} htmlFor="tik-category">{t('myBridge.tik.f_category')}</Label>
              {select('tik-category', draft.category, CATEGORIES, (category) => set({ category }))}
            </div>
          </div>
          {field('tagline', 80)}
          <div className="space-y-1">
            <Label className={label} htmlFor="tik-bio">{t('myBridge.tik.f_bio')}</Label>
            <Textarea id="tik-bio" rows={3} maxLength={400} value={draft.bio} onChange={(e) => set({ bio: e.target.value })} className={input} dir="auto" data-testid="tik-bio" />
          </div>
          <div className="space-y-1">
            <Label className={label}>{t('myBridge.tik.f_services')}</Label>
            {draft.services.map((s, i) => (
              <div key={i} className="flex gap-2">
                <Input aria-label={`${t('myBridge.tik.f_services')} ${i + 1}`} value={s} maxLength={32} onChange={(e) => set({ services: draft.services.map((x, j) => (j === i ? e.target.value : x)) })} className={input} dir="auto" data-testid={`tik-service-${i}`} />
                {draft.services.length > 1 && <Button variant="ghost" size="sm" onClick={() => set({ services: draft.services.filter((_, j) => j !== i) })} aria-label={t('myBridge.tik.remove')}><X className="h-4 w-4" /></Button>}
              </div>
            ))}
            {draft.services.length < 3 && <Button variant="ghost" size="sm" onClick={() => set({ services: [...draft.services, ''] })}><Plus className="h-3.5 w-3.5" /> {t('myBridge.tik.addService')}</Button>}
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label className={label} htmlFor="tik-color">{t('myBridge.tik.f_color')}</Label>
              <input id="tik-color" type="color" value={draft.look.color} onChange={(e) => setLook({ color: e.target.value.toUpperCase() })} className="h-10 w-full cursor-pointer rounded-md border" data-testid="tik-color" />
            </div>
            <div className="space-y-1">
              <Label className={label} htmlFor="tik-shape">{t('myBridge.tik.f_shape')}</Label>
              {select('tik-shape', draft.look.shape, SHAPES, (shape) => setLook({ shape }))}
            </div>
            <div className="space-y-1">
              <Label className={label} htmlFor="tik-accessory">{t('myBridge.tik.f_accessory')}</Label>
              {select('tik-accessory', draft.look.accessory, ACCESSORIES, (accessory) => setLook({ accessory }))}
            </div>
          </div>

          {field('starter', 120)}
          <div className="space-y-1">
            <Label className={label}>{t('myBridge.tik.f_sampleChat')}</Label>
            <p className={hint}>{t('myBridge.tik.sampleChatHint')}</p>
            {draft.sampleChat.map((l, i) => (
              <div key={i} className="flex gap-2">
                <select aria-label={t('myBridge.tik.f_sampleChat')} value={l.from} onChange={(e) => set({ sampleChat: draft.sampleChat.map((x, j) => (j === i ? { ...x, from: e.target.value as 'user' | 'tik' } : x)) })} className={`h-10 rounded-md border px-2 text-sm ${input}`}>
                  <option value="user">{t('myBridge.tik.fromUser')}</option>
                  <option value="tik">{draft.name || t('myBridge.tik.fromTik')}</option>
                </select>
                <Input aria-label={`${t('myBridge.tik.f_sampleChat')} ${i + 1}`} value={l.text ?? ''} maxLength={240} onChange={(e) => set({ sampleChat: draft.sampleChat.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) })} className={input} dir="auto" />
                <Button variant="ghost" size="sm" onClick={() => set({ sampleChat: draft.sampleChat.filter((_, j) => j !== i) })} aria-label={t('myBridge.tik.remove')}><X className="h-4 w-4" /></Button>
              </div>
            ))}
            {draft.sampleChat.length < MAX_LINES && (
              <Button variant="ghost" size="sm" onClick={() => set({ sampleChat: [...draft.sampleChat, { from: draft.sampleChat[draft.sampleChat.length - 1]?.from === 'user' ? 'tik' : 'user', text: '' }] })}>
                <Plus className="h-3.5 w-3.5" /> {t('myBridge.tik.addLine')}
              </Button>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={fillWithAi} disabled={drafting} data-testid="tik-redraft-ai">
            {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {t('myBridge.tik.redraftAi')}
          </Button>
        </div>

        <div className="space-y-2">
          <div className="flex gap-1 rounded-full bg-ai-muted p-1 text-xs font-semibold">
            {(['card', 'profile'] as const).map((v) => (
              <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)} className={`flex-1 rounded-full px-3 py-1.5 ${view === v ? 'bg-ai-surface shadow-sm' : ''}`}>{t(`myBridge.tik.view_${v}`)}</button>
            ))}
          </div>
          {canFramePreview() ? (
            <iframe
              ref={frame}
              aria-label={t('myBridge.tik.previewTitle')}
              src={`${SITE}/tiks/preview`}
              className="h-[620px] w-full rounded-ai-card border border-ai-border bg-[#FFF6E9]"
              data-testid="tik-preview"
            />
          ) : (
            <a href={`${appConfig.APP_ENDPOINT}/agents/${encodeURIComponent(agentId)}`} target="_blank" rel="noreferrer" className={`block rounded-ai-card border border-ai-border p-4 text-sm ${hint}`} data-testid="tik-preview-link">
              {t('myBridge.tik.previewInApp')}
            </a>
          )}
        </div>
      </div>

      {missing.length > 0 && (
        <ul className="space-y-1 rounded-ai-card bg-ai-muted p-3 text-sm" data-testid="tik-missing">
          {missing.map((m) => <li key={m}>• {t(`myBridge.tik.missing_${m}`)}</li>)}
        </ul>
      )}

      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" data-testid="tik-consent" />
        <span>{t('myBridge.tik.consent')}</span>
      </label>

      {error && <p className="text-sm text-red-500" data-testid="tik-error">{error}</p>}
      <div className="flex flex-wrap items-center gap-2">
        {stored?.published ? (
          <>
            <Button variant="ai" size="sm" onClick={() => save()} disabled={saving || !consent} data-testid="tik-save">{saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {t('myBridge.tik.saveChanges')}</Button>
            <Button variant="ghost" size="sm" onClick={() => save(false)} disabled={saving} data-testid="tik-unpublish">{t('myBridge.tik.unpublish')}</Button>
          </>
        ) : (
          <>
            <Button variant="ai" size="sm" onClick={() => save(true)} disabled={saving || !consent} data-testid="tik-publish">{saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {t('myBridge.tik.publish')}</Button>
            <Button variant="ghost" size="sm" onClick={() => save(false)} disabled={saving} data-testid="tik-save-draft">{t('myBridge.tik.saveDraft')}</Button>
          </>
        )}
        {stored?.published && <span className={hint}>{t('myBridge.tik.reviewHint')}</span>}
        {stored && (
          <Button variant="ghost" size="sm" className="ms-auto text-red-500" onClick={remove} disabled={deleting} data-testid="tik-delete">
            <Trash2 className="h-3.5 w-3.5" /> {t('myBridge.tik.delete')}
          </Button>
        )}
      </div>
    </div>
  );
};

/** Listed / waiting for review / hidden / draft — from the backend's derived status. */
const TikStatusBadge: React.FC<{ status: AgentTik['status']; published: boolean }> = ({ status, published }) => {
  const { t } = useTranslation(namespace);
  if (!status) return null;
  const key = status.listed ? 'listed' : !published ? 'draft' : status.missing.length === 1 && status.missing[0] === 'approval' ? 'review' : 'hidden';
  const cls = { listed: 'bg-green-100 text-green-800', review: 'bg-amber-100 text-amber-800', hidden: 'bg-red-100 text-red-700', draft: 'bg-ai-muted text-ai-text-secondary' }[key];
  return <span className={`rounded-ai-pill px-2.5 py-0.5 text-xs font-semibold ${cls}`} data-testid="tik-status" data-status={key}>{t(`myBridge.tik.status_${key}`)}</span>;
};
